/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, zip, iif, EMPTY } from 'rxjs';
import { map, switchMap, mergeMap, catchError, concatMap, take, tap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { Action, Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';

import { FlightResultsService } from '@flight-results/services/flight-results.service';
import { homeRootRoute } from '@home/home-routing.module';
import { fetchMessageAction } from '@app/store/actions/global.actions';
import { ToastrTypes } from '@app/entities/toastr';
import * as actions from '@flight-results/store/actions/flights-results.actions';
import { AuthService } from '@app/services/auth.service';
import { saveSearchMapper } from '@flight-results/mappers/save-search-payload.mapper';
import { SearchParamFromType } from '@search-bar/entities/search-type';
import { AppFacade } from '@app/app.facade';
import { WidgetsFacade } from '@shared/widgets/store/widgets.facade';
import { flexDatesMapper } from '@flight-results/mappers/flex-dates.mapper';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TriggerAmplitudeEntity } from '@amplitude/interfaces/trigger-entity';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { NoResultsTypes } from '@shared/no-results/entities/results-errors';
import { UserService } from '@app/modules/core/services/user.service';
import { StatsigExperimentsFacade } from '@statsig/statsig.facade';
import { STATSIG_EXPERIMENTS } from '@statsig/statsig.config';
import { mapFiltersSearchFlights } from '@flight-results/mappers/filters.mapper';
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { saveRecentSearchAction } from '@recent-searches/store/actions/recent-searches.actions';
import { RecentSearchDataType } from '@design-system/components/molecules/recent-search/entities/recent-search.types';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { AvoService } from '@avo/services/avo.service';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { getErrorMessage } from '@app/util/error-messages';
import { customParamsMapper } from '@app/mappers/custom-params.mapper';
import { State } from '@app/store/state/state';


import { NewAirSearchPayload } from '@flight-results/entities/request-search.payload';
import { NewAirResultsResponse } from '@app/entities/flight-results';
import { getSearchFlowType, getTravelSegment } from '@app/mappers/by-sector-enabled.mapper';

@Injectable()
export class SearchFlightsEffect {

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private readonly store: Store<State>,
    private readonly action$: Actions,
    private readonly service: FlightResultsService,
    private readonly router: Router,
    private readonly translateService: TranslocoService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly widgetsFacade: WidgetsFacade,
    private readonly appFacade: AppFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly propertiesAmplitudeService: PropertiesAmplitudeService,
    private readonly cookieService: CookieService,
    private readonly statsigExperimentsFacade: StatsigExperimentsFacade,
    private readonly avoService: AvoService,
  ) { }

  private get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$; 
  }

  private get textAlertErrorSearchFlights(): string {
    return this.translateService.translate('FLIGHT_RESULTS_MODULE.FLIGHT_RESULTS_ERROR');
  }

  fetchUserSessionToken$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.searchFlightsAction),
      switchMap(action => {
        const token = this.authService.authToken();
        const SeamlessLoginToken = this.cookieService.get('SeamlessLogin');
        return iif(() => !!SeamlessLoginToken,
          of(SeamlessLoginToken),
          iif(
            () => !!token,
            this.userService.seamlessLogin(token),
            this.userService.requestSession()
          )
        ).pipe(
          map(response => actions.mapSearchPayloadToken({
            payload: {
              ...action.payload,
              SessionToken: response
            }
          })),
          catchError((error) => {
            return this.avoService.getEventTriggerProps$(AVO_EVENTS.TRAVEL_SEAMLESS_ERROR, {
              ERROR_MESSAGE: getErrorMessage(error)
            }).pipe(
              take(1),
              tap(triggerEventBySearchFlight => {
                if (token) {
                  this.AvoDispatch.travelSeamlessError({
                    ...this.avoService.avoBaseProperties(triggerEventBySearchFlight),
                    ...this.avoService.avoSearchProps(triggerEventBySearchFlight?.SEARCH_PROPS),
                    errorMessage: triggerEventBySearchFlight?.EVENT_PROPS?.ERROR_MESSAGE as string
                  });
                }
                this.router.navigate([homeRootRoute]);
              }),
              switchMap(() => [
                fetchMessageAction({
                  toastr: {
                    message: this.textAlertErrorSearchFlights,
                    type: ToastrTypes.WARNING
                  }
                })
              ])
            );
          })
        );
      })
    ));

  mapAirSearchPayload$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.mapSearchPayloadToken),
      switchMap(action => zip(
        this.isMobile$.pipe(take(1)),
        this.appFacade.user$,
        this.statsigExperimentsFacade.userExperiments$,
        this.statsigExperimentsFacade.specificExperiment$(STATSIG_EXPERIMENTS.FEE_SENSITIVITY)
      ).pipe(
        map(([isMobile, userInfo, userExperiments, feeSensitivityExperiment]) => {
          const { payload: { AirSearchPayload, SearchBySector, OptionSelectionIds, SessionToken, FlightKey } } = action;
          const searchPayload: NewAirSearchPayload = {
            SessionToken,
            IsOnlyResults: false,
            IsAirGroup: !isMobile,
            Search: {
              ...AirSearchPayload, 
              RequestSettings: {
                ...AirSearchPayload.RequestSettings,
                BusinessUnitCode: feeSensitivityExperiment?.treatment?.toString() || 
                  AirSearchPayload.RequestSettings.BusinessUnitCode,
                PromoCode: userInfo?.promo_code,
                CustomParams: customParamsMapper(userInfo, userExperiments)
              }
            },
            Results: {
              FlightKey: !isNullOrUndefined(FlightKey) ? decodeURI(FlightKey) : undefined,
              OptionSelectionIds: OptionSelectionIds || [],
              ResultCountLowerBound: 0,
              ResultCountUpperBound: isMobile ? 60 : 20,
              OrderDesc: false,
              OrderCriteria: null,
              Airlines: [],
              Alliances: [],
              BusClass: [],
              Stops: [],
              SectorDurations: [],
              DepartureTimes: [],
              PriceFrom: undefined,
              PriceTo: undefined,
              DepartureAirports: [],
              ArrivalAirports: [],
              Tags: []
            }
          };
          return actions.fetchSearchAndFiltersAction(
            { payload: searchPayload , SearchBySector, isMobile  }
          );
        }))
      )
    ));

  firstFetchAirResults$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchSearchAndFiltersAction),
      switchMap((action) => {
        const { isMobile, SearchBySector, payload} = action;
        return iif(
          () => SearchBySector,
          this.service.searchBySector({
            ...payload,
            IsAirGroup: undefined,
            Search: {
              ...payload.Search,
              AirSearchDomesticType: 'MuliplesOneWayInMultiDestination',
              AirSearchInternationalType: 'MuliplesOneWayInMultiDestination'
            }
          }),
          this.service.search(payload)
        ).pipe(
          mergeMap((response) => {
            const fetchAirFiltersAction = this.fetchAirFiltersAction(SearchBySector); 
            const searchHasResults = (!SearchBySector && response?.ResultsCount > 0) || (SearchBySector && response);
            const notResultAction = !searchHasResults ? 
              [actions.noResultsSearchFlightsAction()] :
              [fetchAirFiltersAction({
                payload: { SearchId: response.SearchId, SessionToken: action.payload.SessionToken },
                results: response
              })];
            return [
              ...notResultAction,
              actions.saveFlightSearchData({
                data: {
                  ResultsAvailableUntil: response.ResultsAvailableUntil,
                  ResultsCount: response?.ResultsCount,
                  SectorsCount: response?.SectorsCount
                }
              }),
              saveRecentSearchAction({
                payload: saveSearchMapper(payload.Search),
                from: payload.Search.RequestSettings.From as SearchParamFromType,
                dataType: RecentSearchDataType.Flight
              })
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            return this.registerAmplitudeSearchError$(error).pipe(
              tap(triggerTravelSearchError => {
                this.AvoDispatch.travelSearchError({
                  ...this.avoService.avoBaseProperties(triggerTravelSearchError),
                  ...this.avoService.avoSearchProps(triggerTravelSearchError?.SEARCH_PROPS),
                  errorMessage: triggerTravelSearchError?.EVENT_PROPS?.ERROR_MESSAGE as string,
                  searchFlowType: getSearchFlowType(SearchBySector),
                  travelSegment: getTravelSegment(SearchBySector, payload?.Results?.OptionSelectionIds),
                });
              }),
              map(() => {
                return this.searchErrorAction(isMobile, error);
              })
            );
          })
        )
      })
    )
  );

  fetchAirGroupResults$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchAirGroupResultsAction),
      switchMap(action => this.service.search({...action.payload, IsAirGroup: true, IsOnlyResults: true}).pipe(
        map(results => {
          if (action.fetchFilters) {
            return actions.fetchFiltersAction({
              payload: {
                SearchId: action?.payload?.Results.SearchId,
                SessionToken: action?.payload?.SessionToken,
              },
              results: results
            });
          } else {
            return actions.fetchAirGroupResultsSuccessAction({ flightResults: results });
          }
        }),
        catchError((error: HttpErrorResponse) => {
          if (error?.error?.code === NoResultsTypes.noresults) {
            return [actions.fetchAirGroupResultsSuccessAction(
              { flightResults: { Results: {AirGroupedResults: []} } as NewAirResultsResponse }
            )];
          } else return [actions.searchFlightsForDesktopErrorAction({ payload: error })];
        })
      ))
    ));

  fetchAirResults$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchAirResultsAction),
      switchMap(action => this.service.search({...action.payload, IsAirGroup: false, IsOnlyResults: true}).pipe(
        switchMap(results => zip(
          of(results),
          this.appFacade.searchData$.pipe(take(1)),
          this.widgetsFacade.saveThisSearch$.pipe(take(1))
        )),
        map(([results]) => {
          if (action.fetchFilters) {
            return actions.fetchFiltersAction({
              payload: {
                SearchId: action?.payload?.Results.SearchId,
                SessionToken: action?.payload?.SessionToken,
              },
              results: results
            });
          } else {                      
            return actions.fetchAirResultsSuccessAction(
              { flightResults: results }
            );
          }
        }),
        catchError((error: HttpErrorResponse) => [actions.searchFlightsForMobileErrorAction({ payload: error })])
      ))
    ));

  fetchAirResultsBySector$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchAirResultsBySectorAction),
      switchMap(action => this.service.searchBySector({...action.payload, IsOnlyResults: true}).pipe(
        map((results) => {
          if (action.fetchFilters) {
            return actions.fetchFiltersBySectorAction({
              payload: {
                SearchId: action?.payload?.Results.SearchId,
                SessionToken: action?.payload?.SessionToken,
              },
              results: results
            });
          } else {
            return actions.fetchAirResultsBySectorSuccessAction({ flightResults: results});
          }
        }),
        catchError((error: HttpErrorResponse) => [actions.searchFlightsForMobileErrorAction({ payload: error })])
      ))
    ));

  searchFlexDates$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.searchFlexDatesAction),
      concatMap(action => {
        const token = this.authService.authToken();
        const SeamlessLoginToken = this.cookieService.get('SeamlessLogin');
        return iif(
          () => !!SeamlessLoginToken,
          of(SeamlessLoginToken),
          iif(
            () => !!token,
            this.userService.seamlessLogin(token),
            this.userService.requestSession()
          ).pipe(
            catchError((e) => {
              return this.avoService.getEventTriggerProps$(AVO_EVENTS.TRAVEL_SEAMLESS_ERROR, {
                ERROR_MESSAGE: getErrorMessage(e)
              }).pipe(
                take(1),
                tap(trigger => {
                  if (token) {
                    this.AvoDispatch.travelSeamlessError({
                      ...this.avoService.avoBaseProperties(trigger),
                      ...this.avoService.avoSearchProps(trigger?.SEARCH_PROPS),
                      errorMessage: trigger?.EVENT_PROPS?.ERROR_MESSAGE as string
                    });
                  }
                  this.store.dispatch(
                    fetchMessageAction({
                      toastr: {
                        message: this.textAlertErrorSearchFlights,
                        type: ToastrTypes.WARNING
                      }
                    })
                  );
                  this.store.dispatch(actions.searchFlexDatesErrorAction({ payload: e }));
                }),
                map(() => '')
              );
            })
          )
        ).pipe(
          switchMap((sessionToken) => iif(
            () => !isEmpty(sessionToken),
            zip(this.service.searchFlexDates({ ...action.payload, SessionToken: sessionToken }), this.isMobile$).pipe(
              map(([results, isMobile]) => {
                const paramsAreValid = !isNullOrUndefined(results?.OriginalDeparture) &&
                !isNullOrUndefined(results?.ByPrice) && !isNullOrUndefined(results?.Results);
                if (paramsAreValid) {
                  return isMobile ? actions.searchFlexDatesMobileSuccessAction({
                    flexDatesMappedResults: {
                      ...flexDatesMapper(results, action.payload),
                      BranchCode: action.payload.RequestSettings.BranchCode
                    }
                  }) : actions.searchFlexDatesSuccessAction({
                    flexDatesResults: { ...results, BranchCode: action.payload.RequestSettings.BranchCode }
                  });
                } else {
                  return actions.searchFlexDatesErrorAction({ payload: null });
                }
              }),
              catchError((error: HttpErrorResponse) => {
                return [actions.searchFlexDatesErrorAction({ payload: error })];
              })
            ),
            EMPTY
          ))
        );
      })
    ));

  fetchFilters$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchFiltersAction),
      concatMap(action => this.isMobile$.pipe(
        take(1),
        switchMap(isMobile => this.service.fetchFilters(action.payload).pipe(
          switchMap(results => [
            actions.fetchFiltersSuccessAction({ filters: mapFiltersSearchFlights(results , results?.Results) }),
            isMobile ? 
              actions.fetchAirResultsSuccessAction({ flightResults: action.results }) : 
              actions.fetchAirGroupResultsSuccessAction({ flightResults: action.results })
          ]),
          catchError((error: HttpErrorResponse) => {
            return isMobile ?
              [actions.searchFlightsForMobileErrorAction({ payload: error }), actions.clearFilterAction()] :
              [actions.searchFlightsForDesktopErrorAction({ payload: error }), actions.clearFilterAction()];
          })
        ))
      ))
    ));

  fetchFiltersBySector$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchFiltersBySectorAction),
      concatMap(action => this.isMobile$.pipe(
        take(1),
        switchMap(isMobile => this.service.fetchFiltersBySector(action.payload).pipe(
          switchMap(results => [
            actions.fetchFiltersSuccessAction({ filters: mapFiltersSearchFlights(results, results?.Results) }),
            actions.fetchAirResultsBySectorSuccessAction({ flightResults: action.results }),
          ]),
          catchError((error: HttpErrorResponse) => {
            return isMobile ?
              [actions.searchFlightsForMobileErrorAction({ payload: error }), actions.clearFilterAction()] :
              [actions.searchFlightsForDesktopErrorAction({ payload: error }), actions.clearFilterAction()];
          })
        ))
      ))
    ));

  private registerAmplitudeSearchError$(payload: HttpErrorResponse): Observable<TriggerAmplitudeEntity> {
    return this.propertiesAmplitudeService.searchProperties$(SearchBy.FLIGHTS).pipe(
      take(1),
      switchMap(searchProps => this.avoService.getEventTriggerProps$(
        AVO_EVENTS.TRAVEL_SEARCH_ERROR, { ERROR_MESSAGE: getErrorMessage(payload) }, searchProps
      ).pipe(take(1)))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private fetchAirFiltersAction(searchBySector: boolean): any {
    return searchBySector ? actions.fetchFiltersBySectorAction : actions.fetchFiltersAction;
  }

  private searchErrorAction(isMobile: boolean, payload: HttpErrorResponse): Action {
    if (payload?.error?.code === NoResultsTypes.noresults && !isMobile) {
      return actions.fetchAirGroupResultsSuccessAction(
        { flightResults: { Results: {AirGroupedResults: []} } as NewAirResultsResponse }
      );
    } else {
      return isMobile ? actions.searchFlightsForMobileErrorAction({ payload }) :
        actions.searchFlightsForDesktopErrorAction({ payload });
    }
  }

}
